import { NotImplementedError } from "../../../../errors";
import { OVERVIEW_BREAKDOWN_DIMENSION_FIELDS, OVERVIEW_BREAKDOWN_METRIC_FIELDS, OVERVIEW_BREAKDOWN_METRIC_FIELD_LIST, parseDateFilter } from "../../../../models/looker/modules/overview";
import { PromiseHandler } from "../../../../handlers";
import { OVERVIEW_BREAKDOWN_QUERY_BASE } from "./query";
/**
 * Typed handler powering every "metric-by-dimension" Overview breakdown widget
 * (Device Distribution, Line Item, Daypart, and future dimensions). One query
 * shape serves them all: it fetches every metric measure (impressions, clicks,
 * conversions, completions) and surfaces the selected one, so only the grouping
 * dimension field varies (see {@link OVERVIEW_BREAKDOWN_DIMENSION_FIELDS}). The
 * frontend calls this through a typed handler rather than building a raw query
 * envelope.
 */
export default class LookerOverviewBreakdown extends PromiseHandler {
    constructor(sdk) {
        super(sdk, "looker-overview-breakdown");
    }
    async findItems(filters) {
        const dateFilter = filters.where?.find((filter) => filter.field === "dates");
        if (!dateFilter) {
            throw new Error("Dates filter is required");
        }
        const dimension = filters.where?.find((filter) => filter.field === "dimension")?.value;
        if (!dimension || !OVERVIEW_BREAKDOWN_DIMENSION_FIELDS[dimension]) {
            throw new Error("A valid breakdown dimension is required");
        }
        const metric = filters.where?.find((filter) => filter.field === "metric")?.value;
        if (!metric || !OVERVIEW_BREAKDOWN_METRIC_FIELDS[metric]) {
            throw new Error("A valid breakdown metric is required");
        }
        const { field: dimensionField, sortField, limit, extraFilters, params } = OVERVIEW_BREAKDOWN_DIMENSION_FIELDS[dimension];
        const metricField = OVERVIEW_BREAKDOWN_METRIC_FIELDS[metric];
        const queryBody = {
            ...OVERVIEW_BREAKDOWN_QUERY_BASE,
            ...(limit ? { limit } : {}),
            // Fetch every metric measure (per the Looker spec); the handler surfaces
            // the selected one, so switching metrics needs no query change.
            fields: [dimensionField, ...OVERVIEW_BREAKDOWN_METRIC_FIELD_LIST],
            // Order by the dimension when it defines one (e.g. daypart); otherwise
            // rank buckets by the selected metric so the biggest lead.
            sorts: [sortField ?? `${metricField} desc`],
            filters: {
                "explore_overview.flt_date_range": parseDateFilter(dateFilter),
                ...extraFilters,
                ...params
            }
        };
        const lookbackFilter = filters.where?.find((filter) => filter.field === "lookbackDays");
        if (lookbackFilter?.value !== undefined && lookbackFilter.value !== null) {
            queryBody.filters["explore_overview.pmt_lookback_window"] = String(lookbackFilter.value);
        }
        const mediaTypeFilter = filters.where?.find((filter) => filter.field === "mediaType");
        const mediaType = mediaTypeFilter?.value;
        if (mediaType && mediaType.length) {
            queryBody.filters["explore_overview.product_group"] = Array.isArray(mediaType)
                ? mediaType.join(",")
                : mediaType;
        }
        const advertiserFilter = filters.where?.find((filter) => filter.field === "advertiserIds");
        const advertiserIds = advertiserFilter?.value;
        if (advertiserIds?.length) {
            queryBody.filters["meta_inst.advertiser_id"] = advertiserIds.join(",");
        }
        const campaignFilter = filters.where?.find((filter) => filter.field === "campaignIds");
        const campaignIds = campaignFilter?.value;
        if (campaignIds?.length) {
            queryBody.filters["meta_inst.campaign_id"] = campaignIds.join(",");
        }
        // The query fetches every metric measure, so hashQuery is metric-independent,
        // but the cached value is formatted for one metric — include the metric in
        // the key so switching metrics doesn't return the previous metric's rows.
        const hashKey = `${await this.sdk.looker.query.hashQuery(queryBody)}:${metric}`;
        return this.cache.promise(hashKey, () => this.sdk.looker.query
            .run(queryBody)
            .then(({ data, queryMeta }) => [
            {
                data: this.formatResponse(data, dimensionField, metricField),
                queryMeta
            }
        ])
            .catch((e) => {
            throw Object.assign(new Error("Failed to fetch overview dashboard breakdown"), {
                cause: e
            });
        }));
    }
    formatResponse(data, dimensionField, metricField) {
        const rows = data
            .filter((row) => row[dimensionField] !== undefined && row[dimensionField] !== null)
            .map((row) => {
            const label = String(row[dimensionField]);
            return { id: label, label, value: Number(row[metricField]) || 0 };
        });
        // The explore has no percent measure, so compute each bucket's share of the
        // selected-metric total (used by the donut legend and percent-mode bars).
        const total = rows.reduce((sum, row) => sum + row.value, 0);
        return rows.map((row) => ({
            ...row,
            percent: total > 0 ? (row.value / total) * 100 : 0
        }));
    }
    async saveItem() {
        throw new NotImplementedError("saveItem");
    }
    async deleteItem() {
        throw new NotImplementedError("deleteItem");
    }
}
//# sourceMappingURL=Breakdown.js.map